import React, { FC, useState, ChangeEvent, useCallback, useEffect } from 'react'
import styles from 'styles/components/template.module.scss'
import { FirebaseTimestamp, db } from 'firebase/index'
import { State } from 'index'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { Description, MainHeadline, SubDescription } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { FormLabel } from 'components/Label'
import { RadioButton, SelectBox, TextInput, Textarea, PointCardExpirationForm, ImageCrop } from 'components/Form'
import { BlueButton, GreyButton, CautionButton } from 'components/Button'
import { getPointCard } from 'reducks/pointCard/selectors'
import { setPointCardOffer } from 'repos/pointCard/pointCardOffer'
import { PointCardOfferConditionType } from 'types/pointCard/pointCardOffer'
import { useImageUrl, useSelectOptionsAmount, useString } from 'hooks/form'
import { usePointCardExpiration } from 'hooks/pointCard'
import { fromAcquiredDateOptions } from 'constants/pointCard'
import { fetchPointCardOffer, deletePointCardOffer } from 'repos/pointCard/pointCardOffer'
import { openCautionModal } from 'reducks/modal/operations'

type Props = {
  isEdit: boolean
  pointCardOfferId?: string
}

const PointCardOfferTemplate: FC<Props> = (props) => {
  const { isEdit, pointCardOfferId } = props
  const dispatch = useDispatch()
  const pointCard = useSelector((state: State) => getPointCard(state))

  const { imageUrl: iconUrl, setImageUrl: setIconUrl, handleImageUrl: handleIconUrl } = useImageUrl()
  const { state: name, handleState: handleName, setState: setName } = useString<string>('')
  const { state: description, handleState: handleDescription, setState: setDescription } = useString<string>('')
  const { state: note, handleState: handleNote, setState: setNote } = useString<string>('')
  const {
    state: conditionType,
    handleState: handleConditionType,
    setState: setConditionType,
  } = useString<PointCardOfferConditionType>('repeat')
  const [conditionPoints, setConditionPoints] = useState<number>(10)
  const {
    expiration,
    expirationType,
    handleExpirationType,
    handleExpiresAt,
    handleExpirationPeriod,
    setExpiration,
    setExpirationType,
  } = usePointCardExpiration()

  const handleRepeatPoints = (value: string) => {
    if (!/^[0-9]*$/.test(value)) return
    setConditionPoints(parseInt(value))
  }
  const handleThresholdPoints = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value === '') {
      setConditionPoints(0)
    } else if (!/^[0-9]*$/.test(value)) {
      return
    } else {
      setConditionPoints(parseInt(value))
    }
  }

  const repeatPointOptions = useSelectOptionsAmount({
    total: 20,
    unit: 50,
    unitString: 'pt',
    initValue: [10, 20, 30, 40],
  })

  const handleSubmit = async () => {
    if (!pointCard) return
    const ref = pointCardOfferId
      ? db.collection('point_cards').doc(pointCard.ref.id).collection('point_card_offers').doc(pointCardOfferId)
      : db.collection('point_cards').doc(pointCard.ref.id).collection('point_card_offers').doc()
    await setPointCardOffer({
      ref,
      brand_ref: pointCard.brand_ref,
      point_card_ref: db.collection('point_cards').doc(pointCard.ref.id),
      icon_url: iconUrl,
      name,
      description,
      note,
      condition: {
        type: conditionType,
        points: conditionPoints,
      },
      expiration: expiration,
      created_at: FirebaseTimestamp.now(),
      updated_at: FirebaseTimestamp.now(),
    })
    dispatch(push('/point_card'))
  }

  const handleDeletePointCardOffer = async () => {
    if (!pointCardOfferId || !pointCard) return
    try {
      await deletePointCardOffer(pointCard.ref.id, pointCardOfferId)
      dispatch(push('/point_card'))
    } catch (error) {
      console.error(error)
    }
  }

  const handleOpenConfirmModal = useCallback(() => {
    dispatch(
      openCautionModal(
        '削除する',
        'キャンセル',
        `一度削除した特典は、取り消すことが出来ませんがよろしいですか？\n<span style="color: red">※すでにお客さまが獲得した特典は削除されません。</span>`,
        handleDeletePointCardOffer,
        `<p style="text-align: left">特典を削除します。</p>`
      )
    )
  }, [pointCard, pointCardOfferId])

  useEffect(() => {
    ;(async () => {
      if (!isEdit || !pointCardOfferId || !pointCard) return
      const pointCardOffer = await fetchPointCardOffer(pointCard.ref.id, pointCardOfferId)
      if (!pointCardOffer) return
      setIconUrl(pointCardOffer.icon_url)
      setName(pointCardOffer.name)
      setDescription(pointCardOffer.description)
      setNote(pointCardOffer.note)
      setConditionType(pointCardOffer.condition.type)
      setConditionPoints(pointCardOffer.condition.points)
      setExpiration(pointCardOffer.expiration)
      if (pointCardOffer.expiration?.expires_at) {
        setExpirationType('specified_date')
      } else if (pointCardOffer.expiration?.expiration_period) {
        setExpirationType('from_acquired_date')
      } else {
        setExpirationType('no_limit')
      }
    })()
  }, [isEdit, pointCard, pointCardOfferId])

  return (
    <div className={styles.standard}>
      <Spacer size={40} />
      <MainHeadline headline={'特典管理'} />
      <Spacer size={20} />
      <Description description={'必要な情報を登録してください。'} />
      <Spacer size={20} />
      <FormLabel label={'アイコン画像'} required={true} />
      <Spacer size={8} />
      <ImageCrop
        folderPath={`point_cards/${pointCard?.ref.id}/point_card_offers/${pointCardOfferId}`}
        handleImageUrl={handleIconUrl}
        imageSize={{ height: 270, width: 270 }}
        imageUrl={iconUrl}
      />
      <Spacer size={20} />
      <TextInput
        label={'特典名'}
        disabled={false}
        maxLength={15}
        required={true}
        onChange={handleName}
        placeholder={'ケーキプレゼント'}
        size={'small'}
        type={'text'}
        value={name}
      />
      <Spacer size={20} />
      <Textarea
        disabled={false}
        label={'特典説明文'}
        maxLength={60}
        onChange={handleDescription}
        required={false}
        rows={3}
        value={description}
      />
      <Spacer size={20} />
      <Textarea disabled={false} label={'注意事項'} onChange={handleNote} required={false} rows={3} value={note} />
      <Spacer size={20} />
      <FormLabel label={'特典付与タイミング'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'いづれかから設定してください'} />
      <Spacer size={8} />
      <RadioButton
        isChecked={conditionType === 'repeat'}
        label={'獲得ポイントごとに繰り返し付与'}
        onChange={handleConditionType}
        value={'repeat'}
      />
      {conditionType === 'repeat' && (
        <>
          <Spacer size={12} />
          <SelectBox
            options={repeatPointOptions}
            select={handleRepeatPoints}
            value={conditionPoints.toString()}
            size={'medium'}
          />
        </>
      )}
      <Spacer size={12} />
      <RadioButton
        isChecked={conditionType === 'threshold'}
        label={'指定されたポイント数で付与'}
        onChange={handleConditionType}
        value={'threshold'}
      />
      {conditionType === 'threshold' && (
        <>
          <Spacer size={12} />
          <TextInput
            disabled={false}
            required={false}
            onChange={handleThresholdPoints}
            size={'small'}
            type={'tel'}
            value={conditionPoints.toString()}
          />
        </>
      )}
      <Spacer size={20} />
      <FormLabel label={'有効期限'} required={true} />
      <Spacer size={4} />
      <SubDescription description={'特典の有効期限を下記のいづれかから選択してください'} />
      <Spacer size={8} />
      <PointCardExpirationForm
        expiration={expiration}
        expirationType={expirationType}
        fromAcquiredDateOptions={fromAcquiredDateOptions}
        handleExpirationPeriod={handleExpirationPeriod}
        handleExpirationType={handleExpirationType}
        handleExpiresAt={handleExpiresAt}
      />
      <Spacer size={40} />
      <BlueButton
        disabled={!name || !conditionType || !expirationType}
        label={'登録する'}
        onClick={handleSubmit}
        size={'medium'}
        type={'main'}
      />
      {isEdit && (
        <>
          <Spacer size={24} />
          <CautionButton label={'削除する'} onClick={handleOpenConfirmModal} size={'medium'} type={'outlined'} />
        </>
      )}
      <Spacer size={24} />
      <GreyButton
        label={'ポイントカードへ戻る'}
        onClick={() => dispatch(push('/point_card'))}
        size={'medium'}
        type={'outlined'}
      />
      <Spacer size={40} />
    </div>
  )
}

export default PointCardOfferTemplate
